import { base64DecodeBinary } from '@canvas/encoding';
import { createErrorClass } from '@canvas/error';
import {
  RecoverableNetworkError,
  retryWithBackoff,
  UnrecoverableNetworkError,
} from '@canvas/retry';
import { type TokenManager } from '@canvas/token-manager';

import { fetchWithTimeout } from './fetch-with-timeout';

export type RemoteStepsUpdates = {
  updates: string[];
  largeUpdates?: string[];
};

export const RelayURLParseError = createErrorClass('RelayURLParseError');

export function parseRelayUrl(relayUrl: string): { documentId: string; nonce: string } {
  const url = new URL(relayUrl);
  const documentId = url.pathname.split('/')[2] as string | undefined;
  const nonce = url.pathname.split('/')[3] as string | undefined;

  if (!documentId) {
    throw new RelayURLParseError(`Cannot find documentId in relay url`);
  }

  if (!nonce) {
    throw new RelayURLParseError(`Cannot find nonce in relay url`);
  }

  return { documentId, nonce };
}

export const StepsAuthenticationError = createErrorClass('StepsAuthenticationError');

export async function fetchDocumentSteps(
  url: string,
  documentId: string,
  signal: AbortSignal,
  headers?: HeadersInit
) {
  const hostname = new URL(url).hostname;
  const response = await fetchWithTimeout(url, { signal, headers }, { timeout: 30000 });
  if (!response.ok) {
    if (response.status === 401) {
      throw new StepsAuthenticationError();
    } else if (response.status === 408) {
      throw new RecoverableNetworkError(
        `Timed out attempting to fetch document steps for ${documentId}`
      );
    } else if (response.status === 410) {
      throw new RecoverableNetworkError(
        `Attempted fetching stale document steps for ${documentId}`
      );
    } else if (response.status >= 500) {
      throw new RecoverableNetworkError(
        `Received a server error status code ${response.status} from ${hostname}`
      );
    } else {
      throw new UnrecoverableNetworkError(
        `Unrecoverable error status code ${response.status} from ${hostname}`
      );
    }
  }
  return response;
}

async function getRemoteLargeStep(
  signedUrl: string,
  documentId: string,
  signal: AbortSignal
) {
  return await retryWithBackoff({
    callback: async () => {
      return fetchDocumentSteps(signedUrl, documentId, signal)
        .then((response) => response.arrayBuffer())
        .then((arrayBuffer) => new Uint8Array(arrayBuffer));
    },
    retryTimeMs: 500,
    shouldRetry: (error) => error instanceof RecoverableNetworkError,
    maxRetries: 2,
  });
}

export type RefreshTokenFn = () => Promise<void>;

export async function getRemoteStepsUpdate(
  headStep: string,
  documentId: string,
  signal: AbortSignal,
  tokenManager: TokenManager,
  refreshTokenFn?: RefreshTokenFn
): Promise<Uint8Array[]> {
  let response: Response;
  try {
    response = await getRemoteSteps(headStep, documentId, signal, tokenManager);
  } catch (err) {
    if (!signal.aborted && err instanceof StepsAuthenticationError) {
      await refreshTokenFn?.();
    }

    throw err;
  }

  const body: RemoteStepsUpdates = await response.json();

  const binaryUpdates = body.updates.map((base64Update) =>
    base64DecodeBinary(base64Update)
  );

  if (body.largeUpdates && body.largeUpdates.length > 0) {
    const largeBinaryUpdates = await Promise.all(
      body.largeUpdates.map((signedUrl) =>
        getRemoteLargeStep(signedUrl, documentId, signal)
      )
    );
    return binaryUpdates.concat(largeBinaryUpdates);
  }

  return binaryUpdates;
}

/**
 * @summary Get remote steps API - fetch with AuthZ powered by a token
 * crafted by a host product (embedded, EAP version of the application).
 */
export async function getRemoteSteps(
  headStep: string,
  documentId: string,
  signal: AbortSignal,
  tokenManager: TokenManager
) {
  const token = await tokenManager.tokenForDocumentIdOrThrow(documentId);

  const url = new URL(`${process.env.VITE_CANVAS_RELAY_URL}`);
  url.pathname = `/api/public/document/${documentId}/steps/${headStep}`;

  return fetchDocumentSteps(url.href, documentId, signal, {
    'x-access-token': token.accessToken,
  });
}
